import type { ChatFile, ChatFileKind } from "types/chat.type";

export const ATTACHMENT_LIMITS: Record<ChatFileKind, number> = {
  image: 5,
  /** Placeholder — enable when non-image attachments are wired. */
  file: Number.POSITIVE_INFINITY,
};

/** Max size per file (all kinds). */
export const MAX_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024;

export type AttachmentErrorCode = "limit" | "duplicate" | "tooLarge";

export type AttachmentValidationError = {
  code: AttachmentErrorCode;
  kind: ChatFileKind;
  name?: string;
};

export type IncomingAttachment = {
  name: string;
  kind: ChatFileKind;
  file: File;
};

export type ValidateAttachmentsResult = {
  accepted: IncomingAttachment[];
  errors: AttachmentValidationError[];
};

/**
 * Validates incoming attachments against per-kind limits and duplicate names.
 * Extensible: add kinds to ATTACHMENT_LIMITS and pass candidates with that kind.
 */
export const validateIncomingAttachments = (
  existing: Pick<ChatFile, "name" | "kind">[],
  incoming: IncomingAttachment[],
): ValidateAttachmentsResult => {
  const accepted: IncomingAttachment[] = [];
  const errors: AttachmentValidationError[] = [];
  const knownNames = new Set(existing.map((file) => file.name));
  const counts: Partial<Record<ChatFileKind, number>> = {};

  for (const file of existing) {
    counts[file.kind] = (counts[file.kind] ?? 0) + 1;
  }

  let limitReported = false;

  for (const candidate of incoming) {
    if (candidate.file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      errors.push({
        code: "tooLarge",
        kind: candidate.kind,
        name: candidate.name,
      });
      continue;
    }

    if (knownNames.has(candidate.name)) {
      errors.push({
        code: "duplicate",
        kind: candidate.kind,
        name: candidate.name,
      });
      continue;
    }

    const limit = ATTACHMENT_LIMITS[candidate.kind];
    const currentCount = counts[candidate.kind] ?? 0;

    if (currentCount >= limit) {
      if (!limitReported) {
        errors.push({ code: "limit", kind: candidate.kind });
        limitReported = true;
      }
      continue;
    }

    accepted.push(candidate);
    knownNames.add(candidate.name);
    counts[candidate.kind] = currentCount + 1;
  }

  return { accepted, errors };
};

export const createChatFileFromIncoming = (incoming: IncomingAttachment): ChatFile => ({
  id: `${incoming.name}-${incoming.file.size}-${incoming.file.lastModified}-${Math.random().toString(36).slice(2, 9)}`,
  name: incoming.name,
  url: URL.createObjectURL(incoming.file),
  kind: incoming.kind,
  file: incoming.file,
});

export const filesToImageCandidates = (files: File[]): IncomingAttachment[] =>
  files
    .filter((file) => file.type.startsWith("image/"))
    .map((file) => ({
      name: file.name,
      kind: "image" as const,
      file,
    }));
